import { ProFormItemProps } from '@ant-design/pro-form';
import React from 'react';
import { FieldReactions } from '@/infrastructure/schema/reaction-engine';
import { deepMerge } from '@/infrastructure/schema/template-parser';
import { FieldScope } from '@/proto/field';
import { IFilter } from '@/proto/metric';
import { KeyDataType } from '@/proto/token';
import { TdsFromNextGroup } from './components/field-filter-panel/config';
import { SCHEMA_STANDARD_FIELD_POOL } from './standard-field-pool';

/**
 * 表达式字符串，用于表示一个js表达式
 * 格式：{{ expression }}
 *
 * 支持的变量：
 * - $self: 当前字段的值
 * - $form: 当前所有字段的值（对象形式，Record<string, any>）
 * - $values: 当前所有字段的值（与 $form 相同，提供别名以便于理解）
 *
 * @example
 * // 隐藏字段：当某个字段值为空时隐藏
 * hidden: "{{ !$self }}"
 *
 * // 联动显示：当其他字段值为特定值时显示
 * hidden: "{{ $form.fieldName !== 'value' }}"
 *
 * // 初始化值：根据其他字段计算初始值
 * initialValue: "{{ $form.fieldA + $form.fieldB }}"
 */
export type SchemaExpression = `{{${string}}}`;

/**
 * 工具类型：将基础类型与 SchemaExpression 组合
 * 用于简化类型定义，使字段可以接受基础类型或表达式字符串
 *
 * @example
 * type Name = WithExpression<string>; // string | SchemaExpression
 * type Hidden = WithExpression<boolean>; // boolean | SchemaExpression
 */
export type WithExpression<T> = T | SchemaExpression;

type DeepPartial<T> =
  T extends (...args: any[]) => any
    ? T
    : T extends (infer U)[]
      ? DeepPartial<U>[]
      : T extends object
        ? { [K in keyof T]?: DeepPartial<T[K]> }
        : T;

export enum ComponentType {
  Tag = 'tag',
  /** 机型字段（型号 + 营销名称映射），与 Tag 完全解耦 */
  ModelTag = 'modelTag',
  DateCascader = 'dateCascader',
  Custom = 'custom',
  CustomFields = 'customFields',
  Area = 'area',
}

export enum ClearBehavior {
  ClearOnly = 'clear-only',
  ClearAndHide = 'clear-and-hide',
}

export { KeyDataType };


/**
 * FormItem 属性，支持表达式
 * 这些属性会直接传递给 ProFormItem
 */
export type FormItemPropsWithExpression = {
  [K in keyof ProFormItemProps]?: WithExpression<ProFormItemProps[K]>;
};

export interface SchemaFormItem {
  /**
   * 字段名，表示表单内部的字段Key值
   */
  name: WithExpression<string | undefined>;
  /**
   * 字段标签
   */
  label: WithExpression<string>;
  /**
   * FormItem 属性，会直接传递给 ProFormItem
   * 例如：style 等
   */
  formItemProps?: FormItemPropsWithExpression;
  /**
   * 渲染该组件所需要的参数
   */
  componentProps: {
    type: WithExpression<ComponentType>; // 组件类型
    /**
     * 自定义渲染函数（仅当 type 为 Custom 时使用）
     * @param props - 渲染参数
     * @param props.name - 字段名
     * @param props.formValues - 当前表单的所有值
     * @param props.componentProps - 解析后的 componentProps（含 overrides 合并结果），
     *   可用于在公共池 render 中读取外部注入的参数（如 issueType）
     * @returns React 节点
     */
    render?: (props: {
      name: string;
      formValues: Record<string, any>;
      componentProps: Record<string, any>;
    }) => React.ReactNode;
    [key: string]: any; // 组件其他参数
  }

  fieldProps?: WithExpression<{
  /**
   * 字段数据范围，表示该字段的数据范围，用于请求下拉框的数据传参
   * 用string用的是Zone
   * 用number用的是MetricType
   *
   * 约定：
   * - undefined：如果 resolveSchemaProtocol 传了 options.zone，会自动兜底注入 zone
   * - null：显式禁用远程候选值加载（同时阻止 zone 注入）
   */
    fieldScope?: WithExpression<FieldScope | null>; // string | number | null
    /**
     * 字段数据 Key，仅用于请求远程下拉框时指定 FieldKey（告诉后端取哪个字段的候选值）
     * 不传时 TagFilter 回退使用 filterName 作为远程请求 key
     */
    fieldKey?: WithExpression<string>;
    /**
     * Filter Key，用于生成 IFilter.key（非 MAP 类型时）
     * 不传时回退使用 fieldKey（保持向后兼容）
     */
    filterKey?: WithExpression<string>;
    /**
   * 过滤字段名,用于后端查询
   */
    filterName: WithExpression<string>;

    /**
     * 字段数据类型：决定最终 filters[] 中每个 item 的 type
     * 默认按 String 处理（兼容现有只支持 string 协议的组件实现）
     */
    keyDataType?: WithExpression<KeyDataType>;

    /**
   * 自定义转换到filter里的逻辑
   */
    filterTransform?: WithExpression<(fieldKey: string, fieldValue: any, allValues: Record<string, any>) => IFilter[]>;
    /**
   * 自定义从filter里面转换到表单值的逻辑
   */
    filterConvert?: WithExpression<() => void>;

    /**
     * 为 true 时，extractFilters 跳过该字段——字段值不会出现在 filters[] 中。
     * 通常与 valueTransform 搭配使用：字段不走 filter 协议，而是映射为外层 values 上的字段。
     */
    excludeFromFilters?: boolean;

    /**
     * 将当前字段的值映射为外层 values 上的 key-value。
     * 返回的对象会在提交时与 formValues 合并后一起传入 onComputed / genStandardToken。
     *
     * @example
     * // 把 TagFilterValue 映射为 showNew: boolean
     * valueTransform: (value) => ({
     *   showNew: value?.conditions?.[0]?.operator === 'NOTEMPTY',
     * })
     */
    valueTransform?: (fieldValue: any, allValues: Record<string, any>) => Record<string, any>;

    /**
     * 远程候选值加载后的转换函数，用于对下拉选项做二次处理（如单位换算、标签格式化等）。
     * 透传给 TdsFieldSelector 的 optionTransform 属性。
     */
    optionTransform?: (options: any[]) => any[];
  }>;
  /**
   * 是否固吸到常驻区域
   */
  attach: WithExpression<boolean>;
  /**
   * 是否启用批量输入功能
   */
  batchEditMode?: WithExpression<boolean>;
  /**
   * 批量输入提示文案
   */
  batchEditTips?: WithExpression<React.ReactNode>;
  /**
   * Behavior when clearing tag value from form.
   * - clear-only: clear conditions but keep the field visible in the form
   * - clear-and-hide: clear conditions and hide the field (detach from the form)
   */
  clearBehavior?: WithExpression<ClearBehavior>;
  /**
   * 是否隐藏
   */
  hidden: WithExpression<boolean>;
  /**
   * 字段联动配置
   */
  reactions?: FieldReactions;
  /**
   * 对比模式下自动隐藏该字段（hidden=true + attach=false）。
   * 仅在容器层 enableCompare 且用户打开对比模式时生效。
   */
  hiddenWhenCompareMode?: boolean;
  /**
   * 渲染优先级，数值越大越靠前。未指定时默认为 0。
   * 用于控制字段之间的相对位置，同 order 的字段保持 schema 原始顺序。
   * 例：AppVersion order=3、showNew order=2 → showNew 紧跟在 AppVersion 后面。
   */
  order?: WithExpression<number>;
  /**
   * 字段组
   */
  group?: WithExpression<TdsFromNextGroup>;
}

// 将 SchemaFormItem 中的表达式类型去除，得到运行时使用的类型
// 如果某个字段原始类型本身就是 SchemaExpression（例如字符串字段），则保持不变
// 对于像 boolean | SchemaExpression 这样的联合类型，会去掉 SchemaExpression 部分
type StripExpressionUnion<T> =
  [Exclude<T, undefined>] extends [SchemaExpression]
    ? Exclude<T, undefined>
    : Exclude<T, SchemaExpression>;

type DeepStripExpression<T> =
  T extends (infer U)[]
    ? DeepStripExpression<U>[]
    : T extends (...args: any[]) => any
      ? T
      : T extends object
        ? { [K in keyof T]: DeepStripExpression<StripExpressionUnion<T[K]>> }
        : StripExpressionUnion<T>;

/**
 * SchemaFormItem 在表达式求值后的运行时类型。
 * 结构尽量与 SchemaFormItem 保持一致，只是去掉了 WithExpression 带来的表达式联合类型。
 */
export type ResolvedSchemaFormItem = DeepStripExpression<SchemaFormItem>;

/**
 * Simple Schema: 给后端/调用方使用的最小配置，基于标准字段池 + overrides
 */
export interface SchemaFormItemSimpleSchema {
  schemaType: 'simple';
  name: string; // 一般推荐使用 StandardCommonFieldKey 对应的字符串
  // 可选覆盖部分属性（支持深度合并）
  overrides?: DeepPartial<Omit<SchemaFormItem, 'name'>>;
}

/**
 * Full Schema: 完整配置，等价于 SchemaFormItem，只是多了 schemaType 标记，便于后端区分
 */
export interface SchemaFormItemFullSchema extends SchemaFormItem {
  schemaType: 'full';
}

/**
 * 协议层对外暴露的 Schema 类型：
 * - 也可以使用 simple/full 两种带 schemaType 的形式
 */
export type SchemaFormItemProtocol =
  | SchemaFormItemSimpleSchema
  | SchemaFormItemFullSchema;

export interface SchemaProtocolResolveOptions {
  /**
   * 自定义标准字段池，默认使用 SCHEMA_STANDARD_FIELD_POOL
   */
  standardFieldPool?: Record<string, SchemaFormItem>;
  /**
   * 当 schema 未显式指定 fieldScope 时使用的兜底 zone
   */
  zone?: FieldScope;
}

export type SchemaInput = SchemaFormItem | SchemaFormItemProtocol;

const injectZoneFieldScope = (schema: SchemaFormItem, zone?: FieldScope): SchemaFormItem => {
  if (!zone) {
    return schema;
  }

  const { fieldProps } = schema;

  if (fieldProps && typeof fieldProps === 'object' && !Array.isArray(fieldProps)) {
    if (typeof fieldProps.fieldScope === 'undefined') {
      fieldProps.fieldScope = zone;
    }
  }

  return schema;
};

const cloneSchemaItem = (item: SchemaFormItem): SchemaFormItem => deepMerge({}, item);

const resolveSimpleSchema = (
  schema: SchemaFormItemSimpleSchema,
  fieldPool: Record<string, SchemaFormItem>,
): SchemaFormItem => {
  const baseConfig = fieldPool[schema.name];
  if (!baseConfig) {
    throw new Error(`[TdsFormNext] Field "${schema.name}" 不在标准池中`);
  }

  const mergedBase = cloneSchemaItem(baseConfig);
  if (!schema.overrides) {
    return mergedBase;
  }

  const mergedResult = deepMerge(mergedBase, schema.overrides as SchemaFormItem);
  mergedResult.name = schema.name;
  return mergedResult;
};

const resolveFullSchema = (schema: SchemaFormItemFullSchema): SchemaFormItem => {
  const cloned = cloneSchemaItem(schema);
  delete (cloned as any).schemaType;
  return cloned;
};

const isSchemaProtocol = (schema: SchemaInput): schema is SchemaFormItemProtocol => (
  Boolean(schema) && typeof schema === 'object' && 'schemaType' in schema
);

const resolveSchemaInput = (
  schema: SchemaInput,
  options?: SchemaProtocolResolveOptions,
): SchemaFormItem => {
  const fieldPool = options?.standardFieldPool ?? SCHEMA_STANDARD_FIELD_POOL;

  const resolvedSchema = (() => {
    if (isSchemaProtocol(schema)) {
      if (schema.schemaType === 'simple') {
        return resolveSimpleSchema(schema, fieldPool);
      }
      return resolveFullSchema(schema);
    }

    return cloneSchemaItem(schema);
  })();

  return injectZoneFieldScope(resolvedSchema, options?.zone);
};

/**
 * 将 Schema 协议转换为 SchemaFormItem
 */
export const resolveSchemaProtocol = (
  schema: SchemaInput,
  options?: SchemaProtocolResolveOptions,
): SchemaFormItem => resolveSchemaInput(schema, options);

/**
 * 批量转换 Schema 协议
 */
export const resolveSchemaProtocols = (
  schemas: SchemaInput[] = [],
  options?: SchemaProtocolResolveOptions,
): SchemaFormItem[] => {
  const results: SchemaFormItem[] = [];

  schemas.forEach((schema, index) => {
    try {
      const resolved = resolveSchemaInput(schema, options);
      results.push(resolved);
    } catch (error) {
      // 解析单个 schema 失败时，记录错误并跳过该项，避免整个页面白屏
      // eslint-disable-next-line no-console
      console.error(
        '[TdsFormNext] Failed to resolve schema at index:',
        index,
        schema,
        error,
      );
    }
  });

  return results;
};

/**
 * 解析 formList 接口返回的 schema 字符串
 */
export const parseSchemaFromFormList = (formList?: string | null): SchemaInput[] => {
  if (!formList) {
    return [];
  }
  try {
    const parsed = JSON.parse(formList);
    return Array.isArray(parsed) ? (parsed as SchemaInput[]) : [];
  } catch (error) {
    console.error('[TdsFormNext] Failed to parse formList schema:', error);
    return [];
  }
};

/**
 * 直接将 formList 字符串转换为 SchemaFormItem[]
 */
export const resolveSchemaFromFormList = (
  formList?: string | null,
  options?: SchemaProtocolResolveOptions,
): SchemaFormItem[] => {
  const schemas = parseSchemaFromFormList(formList);
  return resolveSchemaProtocols(schemas, options);
};

export { SCHEMA_STANDARD_FIELD_POOL } from './standard-field-pool';
